import { OpeningDetailResponse } from 'api-clients/monolith/models/OpeningDetailResponse';
import clsx from 'clsx';

import ChevronRightIcon from '@/assets/icons/chevron-right.svg';
import CreditCardIcon from '@/assets/icons/credit-card.svg';
import ErrorIcon from '@/assets/icons/exclamation-circle.svg';
import LocationMarkerIcon from '@/assets/icons/location-marker.svg';
import { ClockIcon } from '@/components/icons/clock';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip } from '@/components/ui/tooltip';
import { Typography } from '@/components/ui/typography';
import { useJobPostingContext } from '@/contexts/job-posting-context';

import { useJobConstants } from './use-job-constants';

interface SummaryViewProps {
  opening: OpeningDetailResponse;
  payDisplay: string | null;
  toggleExpanded: () => void;
}

export function SummaryView({
  opening,
  payDisplay,
  toggleExpanded,
}: SummaryViewProps) {
  const { getJobHours } = useJobConstants();
  const { hookForm } = useJobPostingContext();
  const {
    formState: { errors },
    getValues,
  } = hookForm;

  const postingTitle = getValues(`jobDetails.${opening.external_id}.title`);
  const jobDetailErrors = errors?.jobDetails?.[opening.external_id];

  /**
   * Surface the actual schema validation errors for this opening in the tooltip
   * (instead of a hardcoded "job description" message). Field order matches the
   * card layout in `expanded-view.tsx` so the tooltip reads top-to-bottom.
   */
  const errorMessages = [
    jobDetailErrors?.title?.message,
    jobDetailErrors?.description?.message,
    jobDetailErrors?.industry?.message,
    jobDetailErrors?.jobHours?.message,
    jobDetailErrors?.jobType?.message,
    jobDetailErrors?.experience?.message,
    jobDetailErrors?.locationAddress?.message,
    jobDetailErrors?.postalCode?.message,
    jobDetailErrors?.city?.message,
    jobDetailErrors?.stateCode?.message,
  ].filter((message): message is string => typeof message === 'string');

  const hasError = errorMessages.length > 0;

  return (
    <button
      type="button"
      onClick={toggleExpanded}
      className={clsx(
        'border-fx-neutral-200 hover:bg-fx-neutral-50 flex items-center justify-between gap-2 rounded-lg border bg-white p-6 shadow-sm',
        hasError && 'border-fx-rose',
      )}
    >
      <div className="flex flex-col items-start gap-2">
        <Typography variant="body2">{postingTitle}</Typography>

        <div className="grid w-full grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-4">
          {(opening.location_address || opening.remote) && (
            <div className="flex items-center gap-1 overflow-hidden">
              <img
                src={LocationMarkerIcon}
                alt="Location Marker"
                className="shrink-0"
              />
              <Typography variant="subtitle1" className="truncate">
                {opening.location_address || (opening.remote && 'Remote')}
              </Typography>
            </div>
          )}

          {payDisplay && (
            <div className="flex items-center gap-1">
              <img src={CreditCardIcon} alt="Credit Card" />
              <Typography variant="subtitle1">{payDisplay}</Typography>
            </div>
          )}

          {opening.job_hours && (
            <div className="flex items-center gap-1">
              <ClockIcon />
              <Typography variant="subtitle1">
                {getJobHours(opening.job_hours)}
              </Typography>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 justify-self-end">
        {hasError && (
          <Tooltip
            content={
              errorMessages.length === 1 ? (
                errorMessages[0]
              ) : (
                <ul className="flex list-disc flex-col gap-1 pl-4">
                  {errorMessages.map(message => (
                    <li key={message}>{message}</li>
                  ))}
                </ul>
              )
            }
          >
            <img src={ErrorIcon} alt="Error" />
          </Tooltip>
        )}
        <img src={ChevronRightIcon} alt="Chevron right" />
      </div>
    </button>
  );
}

SummaryView.Skeleton = function SummaryViewSkeleton() {
  return (
    <div className="border-fx-neutral-200 flex items-center justify-between gap-2 rounded-lg border bg-white p-6 shadow-sm">
      <div className="flex flex-col items-start gap-2">
        <Skeleton className="h-5 w-[240px]" />

        <div className="mt-1 flex w-full items-center gap-4">
          <Skeleton className="h-4 w-[320px]" />
          <Skeleton className="h-4 w-[80px]" />
          <Skeleton className="h-4 w-[80px]" />
        </div>
      </div>
      <img src={ChevronRightIcon} alt="Chevron right" />
    </div>
  );
};
